import React, { useMemo, useState } from "react"

import { Icon } from "@/components/Icon"

import { ProposedChanges, paragraphsInDiff, parseChangeBlocks } from "./ProposedChanges"

interface ApprovalPanelProps {
  status: string
  proposalSummary: string
  diffText: string
  onApprove: () => void
  onReject: (message?: string) => void
  onEditApprove: (editedSummary: string) => void
  onSteer: (steerMessage: string) => void
  onCancel?: () => Promise<void>
  possibleRetryAfterFailure?: boolean
}

type PanelMode = "review" | "steer" | "reject"

/**
 * The officer's review surface for a proposed rewrite — a bottom drawer, not
 * an overlay: the order stays visible and scrollable above it while the
 * officer decides, and the drawer collapses to a one-line bar so they can
 * read the full order mid-review. Word-processor metaphors only: current text
 * vs revised text, an editable change note, and three decisions — accept,
 * request changes, discard. The header names the order paragraphs (¶) the
 * edit touches, so a change that ripples into other sections is visible
 * before the cards are read. The change note is editable inline; accepting
 * with an edited note sends the backend's "edit" decision, accepting
 * untouched sends "approve".
 */
export const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  status,
  proposalSummary,
  diffText,
  onApprove,
  onReject,
  onEditApprove,
  onSteer,
  onCancel,
  possibleRetryAfterFailure,
}) => {
  const [mode, setMode] = useState<PanelMode>("review")
  const [collapsed, setCollapsed] = useState(false)
  const [editedSummary, setEditedSummary] = useState(proposalSummary)
  const [steerMessage, setSteerMessage] = useState("")
  const [rejectMessage, setRejectMessage] = useState("")

  const touchedParagraphs = useMemo(() => paragraphsInDiff(diffText), [diffText])
  const editCount = useMemo(() => parseChangeBlocks(diffText).length, [diffText])

  if (status !== "awaiting_approval") return null

  const handleAccept = () => {
    const summary = editedSummary.trim()
    if (summary && summary !== proposalSummary.trim()) onEditApprove(summary)
    else onApprove()
  }

  const handleSteer = () => {
    if (!steerMessage.trim()) return
    onSteer(steerMessage.trim())
    setSteerMessage("")
  }

  const handleReject = () => {
    onReject(rejectMessage.trim() || undefined)
    setRejectMessage("")
  }

  if (collapsed) {
    return (
      <div
        className="hitl-panel hitl-panel--collapsed"
        role="dialog"
        aria-label="A proposed edit is awaiting your decision"
      >
        <div className="hitl__bar">
          <span className="hitl__dot" aria-hidden />
          <span className="hitl__bar-title">Proposed edit</span>
          {touchedParagraphs.length > 0 && (
            <span className="hitl__where">¶ {touchedParagraphs.join(" · ")}</span>
          )}
          <p className="hitl__bar-summary" title={proposalSummary}>
            {proposalSummary || `${editCount} change${editCount === 1 ? "" : "s"} to the order`}
          </p>
          <button className="btn btn--primary btn--sm" onClick={() => setCollapsed(false)}>
            Review &amp; decide
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="hitl-panel" role="dialog" aria-label="Review the proposed edit">
      <div className="hitl__head">
        <span className="hitl__dot" aria-hidden />
        <h3>Review the proposed edit</h3>
        {touchedParagraphs.length > 0 && (
          <span className="hitl__where" title="Order paragraphs this edit touches">
            ¶ {touchedParagraphs.join(" · ")}
          </span>
        )}
        <span className="chip chip--run">Awaiting your decision</span>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn--quiet btn--sm"
          onClick={() => setCollapsed(true)}
          aria-label="Minimize the review drawer to read the order"
        >
          <Icon name="chevdown" size={14} /> Minimize
        </button>
        {onCancel && (
          <button className="btn btn--quiet btn--sm" onClick={onCancel}>
            Cancel rewrite
          </button>
        )}
      </div>

      {possibleRetryAfterFailure && (
        <div className="hitl__warn">
          <Icon name="alert" size={15} />
          <p>
            Your previous decision didn&apos;t complete successfully. Please try again, or contact
            support if this keeps happening.
          </p>
        </div>
      )}

      <div className="hitl__body">
        <div className="hitl__section">
          <p className="hitl__label">Change note</p>
          <p className="hitl__hint">
            This note is recorded with the edit. Refine it if the wording doesn&apos;t match what
            the edit does.
          </p>
          <textarea
            className="input hitl__summary"
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            placeholder="Describe what this edit changes…"
            rows={2}
          />
        </div>

        <div className="hitl__section">
          <p className="hitl__label">What will change in the order</p>
          <ProposedChanges diffText={diffText} />
        </div>

        {mode === "steer" && (
          <div className="hitl__mode">
            <p className="hitl__label">Request changes</p>
            <p className="hitl__hint">
              Describe what should be different. NitiAssist will revise the edit and bring it back
              for your review — nothing is applied to the order until you accept.
            </p>
            <textarea
              className="input hitl__summary"
              value={steerMessage}
              onChange={(e) => setSteerMessage(e.target.value)}
              placeholder="e.g. Keep paragraph 3.11 but use the phrase 'issuance of a consolidated notice' throughout…"
              rows={3}
              autoFocus
            />
            <div className="hitl__mode-actions">
              <button
                className="btn btn--primary"
                onClick={handleSteer}
                disabled={!steerMessage.trim()}
              >
                <Icon name="send" size={14} /> Send instructions
              </button>
              <button className="btn btn--quiet" onClick={() => setMode("review")}>
                Back
              </button>
            </div>
          </div>
        )}

        {mode === "reject" && (
          <div className="hitl__mode">
            <p className="hitl__label">Discard this edit</p>
            <p className="hitl__hint">
              The order stays exactly as it is. Optionally note why, so NitiAssist can do better
              next time.
            </p>
            <textarea
              className="input hitl__summary"
              value={rejectMessage}
              onChange={(e) => setRejectMessage(e.target.value)}
              placeholder="Optional: reason for discarding…"
              rows={2}
              autoFocus
            />
            <div className="hitl__mode-actions">
              <button className="btn btn--danger" onClick={handleReject}>
                <Icon name="trash" size={14} /> Discard edit
              </button>
              <button className="btn btn--quiet" onClick={() => setMode("review")}>
                Back
              </button>
            </div>
          </div>
        )}
      </div>

      {mode === "review" && (
        <div className="hitl__actions">
          <button className="btn btn--primary" onClick={handleAccept} aria-label="Accept edit">
            <Icon name="check" size={14} /> Accept &amp; apply to order
          </button>
          <button
            className="btn btn--gold"
            onClick={() => setMode("steer")}
            aria-label="Request changes"
          >
            <Icon name="reply" size={14} /> Request changes
          </button>
          <button
            className="btn btn--ghost"
            onClick={() => setMode("reject")}
            aria-label="Discard edit"
          >
            Discard edit
          </button>
        </div>
      )}
    </div>
  )
}
